import { Grid, TextField, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@mui/material';
import { DateRange } from '../../types/dateRange';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DateRangePicker from '../../components/DateRangePicker';
import ScootersLockHistoryTable from '../../components/ScootersLockHistoryTable';
import Select from '../../components/Select';
import { SelectOption } from '../../components/Select/interface';
import Title from '../../components/Title';
import { useGetAdminsQuery } from '../../redux/api/adminsApi';
import { useGetScootersLockHistoryQuery } from '../../redux/api/scootersApi';
import { InputChangeEvent } from '../../resources/utils';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectCities } from '../../redux/slices/citiesSlice';

const ScootersLockHistoryPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: admins } = useGetAdminsQuery();

  const citiesList = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const userCityId = useMemo(() => {
    const city = citiesList.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, citiesList.items]);

  const [adminId, setAdminId] = useState('');
  const [scooterName, setScooterName] = useState('');
  const [scooterId, setScooterId] = useState('');
  const [dateRange, setDateRange] = useState<DateRange<Moment | null>>([
    null,
    null
  ]);
  const [page, setPage] = useState(1);

  const clearPageWrapper = useCallback(
    <T extends (...args: any[]) => void>(fn: T) => {
      return (...args: Parameters<T>) => {
        fn(...args);
        setPage(1);
      };
    },
    []
  );

  const queryParams = useMemo(() => ({
    page,
    ...(adminId && { adminId }),
    city: userCityId,
    type: 'admin,driver',
    ...(dateRange[0] && { from: dateRange[0].toISOString() }),
    ...(dateRange[1] && { to: dateRange[1].toISOString() }),
    ...(scooterName && { scooterName })
  }), [page, adminId, userCityId, dateRange, scooterName]);

  const { data: scootersLockHistory, isFetching } = useGetScootersLockHistoryQuery(
    queryParams,
    {
      skip: !userCityId,
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    console.log('Query params:', queryParams);
    console.log('Data:', scootersLockHistory);
  }, [queryParams, scootersLockHistory]);

  const adminsSelectOptions = useMemo<SelectOption[]>(
    () =>
      admins
        ? [
            {
              value: '',
              label: t('lockHistory.all')
            },
            ...admins.map(admin => ({
              value: admin._id,
              label: admin.login
            }))
          ]
        : [],
    [admins, t]
  );

  const handleScooterName = (e: InputChangeEvent) => {
    setScooterName(e.target.value);
  };

  useEffect(() => {
    if (scootersLockHistory && scooterName) {
      const history = [
        ...scootersLockHistory.locked,
        ...scootersLockHistory.unlocked
      ];

      const scooterId = history.find(
        scooter => scooter.scooterId?.name === scooterName
      )?.scooterId._id;

      if (scooterId) setScooterId(scooterId);
    }

    if (!scooterName) setScooterId('');
  }, [scooterId, scooterName, scootersLockHistory]);

  if (!scootersLockHistory) {
    return null;
  }

  return (
    <Grid container direction='column' spacing={4} sx={{ opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
      <Grid item>
        <Title>{t('lockHistory.title')}</Title>
      </Grid>
      <Grid item>
        <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
          <Grid container spacing={2}>
            {
              adminsSelectOptions?.length ? (
                <Grid item>
                <Select
                  style={{ minWidth: 140 }}
                  fullWidth
                  label={t('lockHistory.userFilter')}
                  options={adminsSelectOptions}
                  onChange={clearPageWrapper(setAdminId)}
                  value={adminId}
                />
              </Grid>
              ) : null
            }
            <Grid item>
              <DateRangePicker
                startText={t('lockHistory.from')}
                endText={t('lockHistory.to')}
                value={dateRange}
                onChange={(date: DateRange<Moment>) => {
                  date[1]?.add('23:59', 'hours');
                  setDateRange(date);
                  setPage(1);
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                label={t('lockHistory.scooterName')}
                value={scooterName}
                onChange={handleScooterName}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item>
        <Pagination
          page={page}
          count={scootersLockHistory.pages}
          onChange={(event, page) => setPage(page)}
        />
      </Grid>
      <Grid item>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
              <ScootersLockHistoryTable
                isLocked
                rows={scootersLockHistory.locked}
                isScooterId={Boolean(scooterId)}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
              <ScootersLockHistoryTable
                isLocked={false}
                rows={scootersLockHistory.unlocked}
                isScooterId={Boolean(scooterId)}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Pagination
          page={page}
          count={scootersLockHistory.pages}
          onChange={(event, page) => setPage(page)}
        />
      </Grid>
    </Grid>
  );
};

export default ScootersLockHistoryPage;
